import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CalendarCheck,
  CheckCircle2,
  Dumbbell,
  FileText,
  Flag,
  LockKeyhole,
  PlayCircle,
  Sparkles,
  Trophy,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { recordStudentGrowthEvent } from '../api.js'
import { resourcesFromLearningStep } from '../modules/learning/resourceTypes.js'
import { useLearningPlan } from '../modules/learning/useLearningPlan.js'
import { completeLearningStep, getCompletedRoadmapStages, getCurrentLearningStep } from '../student-roadmap.js'

function EmptyStageState() {
  return (
    <main className="page-container student-roadmap-page">
      <section className="student-roadmap-empty">
        <span><Flag size={18} /> Learning OS</span>
        <h1>先生成学习计划，再开始每日学习</h1>
        <p>完成 Onboarding 后，这里会展示知识点、练习、资源和项目。</p>
        <Link className="primary-button" to="/onboarding">30 秒生成学习计划</Link>
      </section>
    </main>
  )
}

function LearningResourceCard({ icon: Icon, resource }) {
  const content = (
    <>
      <Icon size={18} />
      <strong>{resource.title}</strong>
      <p>{resource.description}</p>
    </>
  )

  if (resource.type === 'video' && resource.url) {
    return (
      <a className="learning-os-resource-card" href={resource.url} target="_blank" rel="noreferrer">
        {content}
      </a>
    )
  }

  if (resource.route) {
    return (
      <Link className="learning-os-resource-card" to={resource.route}>
        {content}
      </Link>
    )
  }

  return <article className="learning-os-resource-card">{content}</article>
}

function StudentRoadmapStagePage() {
  const { stageId } = useParams()
  const learning = useLearningPlan()
  const [notice, setNotice] = useState('')

  const stage = learning.roadmap.find((item) => item.id === stageId) || null
  const currentStep = getCurrentLearningStep(stage)
  const resources = resourcesFromLearningStep(currentStep, stage)
  const isDone = Boolean(stage?.completed || learning.completedStageIds.includes(stage?.id))

  async function finishStep() {
    if (!stage || !currentStep) return
    const result = completeLearningStep(learning.plan, stage, currentStep)
    learning.refreshLocalProgress()
    setNotice(`完成 ${currentStep.title}，获得 ${currentStep.xp || 20} XP。`)
    try {
      await recordStudentGrowthEvent({
        planId: learning.plan?.id || undefined,
        eventType: 'learning_step_completed',
        title: `完成学习：${stage.name} / ${currentStep.title}`,
        description: currentStep.exercise,
        metadata: {
          stageId: stage.id,
          stepId: currentStep.id,
          xp: currentStep.xp || 20,
          stageCompleted: result.stageCompleted,
          completedStageIds: getCompletedRoadmapStages(learning.plan),
        },
      })
    } catch {
      // Local Learning OS progress should still work without the timeline API.
    }
  }

  if (!learning.loading && !learning.plan) return <EmptyStageState />

  if (!learning.loading && !stage) {
    return (
      <main className="page-container student-roadmap-page">
        <section className="student-roadmap-empty">
          <span><Flag size={18} /> Learning OS</span>
          <h1>没有找到这个学习阶段</h1>
          <p>返回路线总览，重新选择当前阶段。</p>
          <Link className="primary-button" to="/learning">返回路线</Link>
        </section>
      </main>
    )
  }

  return (
    <main className="page-container student-roadmap-page learning-os-stage-page">
      <div className="student-project-rec-topbar">
        <Link className="secondary-button" to="/growth"><ArrowLeft size={16} /> 返回我的成长</Link>
        <Link className="secondary-button" to="/learning"><ArrowLeft size={16} /> 返回路线</Link>
      </div>

      <section className="student-roadmap-stage-hero learning-os-stage-hero">
        <span><Flag size={18} /> {stage?.name || '学习阶段'}</span>
        <h1>{learning.loading ? '正在加载学习步骤...' : currentStep?.title || `${stage.name} 已完成`}</h1>
        <p>{currentStep ? `今天先完成这个知识点：${currentStep.title}。资源、练习、小测试都在本页。` : '这个阶段的步骤已经完成，可以回到路线继续下一阶段。'}</p>
        <div className="student-growth-summary">
          <span>{isDone ? '阶段完成' : '进行中'}</span>
          <span>{stage?.stepProgress || 0}%</span>
          <span>{learning.xp} XP</span>
        </div>
      </section>

      {learning.error ? <p className="student-growth-error">{learning.error}</p> : null}
      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      {stage ? (
        <>
          <section className="learning-os-step-strip" aria-label="学习步骤">
            {stage.steps.map((step, index) => (
              <article className={`${step.completed ? 'is-done' : ''} ${currentStep?.id === step.id ? 'is-current' : ''}`} key={step.id}>
                <span>{step.completed ? <CheckCircle2 size={15} /> : index + 1}</span>
                <strong>{step.title}</strong>
                <small>{step.minutes} 分钟</small>
              </article>
            ))}
          </section>

          {currentStep ? (
            <section className="learning-os-workspace">
              <article className="learning-os-focus-card">
                <span><CalendarCheck size={18} /> 今日学习</span>
                <h2>{currentStep.title}</h2>
                <ol>
                  <li><strong>1</strong> 学习 {currentStep.minutes} 分钟</li>
                  <li><strong>2</strong> {currentStep.exercise}</li>
                  <li><strong>3</strong> {currentStep.quiz}</li>
                </ol>
                <button className="primary-button" type="button" onClick={finishStep}>
                  完成，获得 {currentStep.xp || 20} XP <ArrowRight size={16} />
                </button>
              </article>

              <div className="learning-os-resource-grid">
                {resources.map((resource) => {
                  const Icon = { video: PlayCircle, doc: FileText, exercise: Dumbbell, project: BookOpenCheck }[resource.type] || FileText
                  return <LearningResourceCard icon={Icon} key={resource.type} resource={resource} />
                })}
              </div>
            </section>
          ) : (
            <section className="learning-os-focus-card">
              <span><CheckCircle2 size={18} /> 阶段完成</span>
              <h2>{stage.name} 已完成</h2>
              <p>回到路线继续下一阶段，或者进入项目学习整理作品。</p>
              <Link className="primary-button" to="/learning">继续路线 <ArrowRight size={16} /></Link>
            </section>
          )}

          <section className="student-roadmap-detail-grid">
            <article className="student-roadmap-detail-card">
              <h2><Trophy size={18} /> 推荐竞赛</h2>
              {stage.competitions?.length ? <ul>{stage.competitions.map((item) => <li key={item}>{item}</li>)}</ul> : <p>当前阶段先完成学习步骤。</p>}
            </article>
            <article className="student-roadmap-detail-card">
              <h2><FileText size={18} /> 推荐证书</h2>
              {stage.certificates?.length ? <ul>{stage.certificates.map((item) => <li key={item}>{item}</li>)}</ul> : <p>当前阶段不建议为了证书分散精力。</p>}
            </article>
            <article className="student-roadmap-detail-card">
              <h2><Sparkles size={18} /> 推荐项目</h2>
              {stage.projects?.length ? <ul>{stage.projects.map((item) => <li key={item}>{item}</li>)}</ul> : <p>完成本阶段后再整理项目。</p>}
            </article>
            <article className="student-roadmap-detail-card">
              <h2><CheckCircle2 size={18} /> 时间线</h2>
              {learning.timeline.length ? <ul>{learning.timeline.slice(0, 4).map((item) => <li key={item.id}>{item.title} +{item.xp}XP</li>)}</ul> : <p>完成一个知识点后会写入时间线。</p>}
            </article>
          </section>

          {stage.locked && !isDone ? (
            <p className="student-roadmap-lock-note"><LockKeyhole size={15} /> 完成前一阶段后自动解锁。</p>
          ) : null}
        </>
      ) : null}
    </main>
  )
}

export default StudentRoadmapStagePage
